<template>
  <!-- #region template -->
  <b-container fluid>
    <b-row>
      <b-col cols="12" class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <!-- #region burger -->
          <div v-bind:class="{ change: navState == NavState.Open }" class="burger mr-4" v-on:click="toggleNavState">
            <div class="bar1"></div>
            <div class="bar2"></div>
            <div class="bar3"></div>
          </div>
          <!-- #endregion -->
          <!-- #region status -->
          <div class="d-flex align-items-center">
            <div class="status mr-3"></div>
            <p class="m-0 text-muted">Configurators are live</p>
          </div>
          <!-- #endregion -->
        </div>
        <div>
          <b-form-input class="search" v-model="search" placeholder="Search options.." @focus="triggerSearch" @input="updateSearch"></b-form-input>
          <div ref="searchBox" v-if="searched" class="search__results">
            <template v-if="search_results.length">
              <template v-for="(result, index) in search_results">
                <div v-on:click="openResult(result)" v class="item" :key="index">
                  <img :src="result.thumbnail" style="width: 50px; height: 50px" />
                  <div>
                    <p class="name">{{ result.name }}</p>
                    <div class="meta">
                      <p>{{ result.product }}</p>
                      <p>|</p>
                      <p>{{ result.category }}</p>
                      <p>|</p>
                      <p class="m-0" :class="{ 'text-success': result.available, 'text-warning': !result.available }">{{ result.available ? 'Published' : 'Draft' }}</p>
                    </div>
                  </div>
                </div>
              </template>
            </template>
            <template v-else>
              <p class="empty">No search results found. Please try something else.</p>
            </template>
          </div>
        </div>
        <!-- #region dropdown -->
        <b-dropdown size="lg" block variant="link" right toggle-class="text-decoration-none p-0" no-caret>
          <template #button-content>
            <div class="d-flex align-items-center text-muted">
              <b-icon icon="person-circle" aria-hidden="true"></b-icon>
              <p class="m-0"></p>
            </div>
          </template>
          <b-dropdown-item class="my-2" v-on:click="$refs['publish-modal'].toggle()">Publish</b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item to="/help">Help</b-dropdown-item>
          <b-dropdown-item to="/support">Support</b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item class="my-2" to="/usermanagement?mode=signOut">Sign out</b-dropdown-item>
        </b-dropdown>
        <!-- #endregion -->
      </b-col>
      <!-- #region publish-modal -->
      <b-modal ref="publish-modal" header-bg-variant="primary" header-text-variant="light" title="Publish">
        <p class="mb-0">You are about to publish your changes. Are you sure you want to continue?</p>
        <template #modal-footer>
          <b-button variant="secondary" @click="$refs['publish-modal'].toggle()" :disabled="busy">Cancel</b-button>
          <b-button variant="primary" @click="publish" :disabled="busy">
            <template v-if="!busy"> Yes </template>
            <template v-else> <b-spinner small type="grow"></b-spinner> Publishing... </template>
          </b-button>
        </template>
      </b-modal>
      <!-- #endregion -->
      <!-- #region search-form-modal -->
      <b-modal hide-footer ref="edit-modal" size="xl" :title="'Edit option'" body-class="p-5">
        <cForm @close="$refs['edit-modal'].toggle()" type="edit" :blueprint="selected_search_result.blueprint" :init="selected_search_result.document" :documents="[]" />
      </b-modal>
      <!-- #endregion -->
    </b-row>
  </b-container>
  <!-- #endregion -->
</template>

<script>
// #region script
// #region import
import cForm from '@/components/cForm'
import { mapMutations, mapState, mapActions } from 'vuex'
import { SET_NAV_STATE } from '@/plugins/store/mutations'
import { NavState } from '@/utils'
// #endregion

// #region export
export default {
  name: 'cHeader',
  components: {
    cForm,
  },
  data() {
    return {
      NavState,
      busy: false,
      search: '',
      timeout: null,
      searched: false,
      search_results: [],
      selected_search_result: {},
      blueprint: null,
    }
  },
  computed: {
    ...mapState(['navState']),
  },
  async mounted() {
    try {
      this.blueprint = await this['firestore/getDoc']({
        path: '_blueprints/w28raF7U6DUPgtq58kF6',
      })
    } catch (error) {
      console.error('Error:', error)
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    hide(bvEvent) {
      if (!this.busy) {
        bvEvent.preventDefault()
      }
    },
    ...mapActions(['firestore/publish', 'firestore/getDocsBySearchQuery', 'firestore/getDoc']),
    ...mapMutations([SET_NAV_STATE]),
    /**
     * Toggles the current navState.
     */
    toggleNavState() {
      this[SET_NAV_STATE](this.navState == NavState.Open ? NavState.Closed : NavState.Open)
    },
    /**
     * Publish the changes to the cache.
     */
    publish() {
      this.busy = true
      // * We do finally here, because we do not care about whether or not the promise resolved or rejected. The action themselves should always be responsible for handling potential errors.
      this['firestore/publish']().finally(() => {
        this.$refs['publish-modal'].toggle()
        this.busy = false
      })
    },
    /**
     * Called when the search input field is updated.
     */
    updateSearch(value) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.search = value
        this.triggerSearch()
      }, 500) // delay of 500 ms
    },
    /**
     * Triggers a new search after delay
     */
    async triggerSearch() {
      this.search_results = []
      if (this.search) {
        try {
          const results = await this['firestore/getDocsBySearchQuery']({
            path: 'options',
            search_query: this.search,
          })
          const populatedResults = await Promise.all(
            results.map(async (result) => {
              const product = await this['firestore/getDoc']({
                path: 'products/' + result.product.id,
              })
              const category = await this['firestore/getDoc']({
                path: 'categories/' + result.category.id,
              })
              return { ...result, product, category }
            })
          )
          populatedResults.forEach((result) => {
            this.search_results.push({
              thumbnail: result.thumbnail,
              name: result.name,
              product: result.product.name,
              category: result.category.name,
              available: result.available,
              document: result,
              blueprint: this.blueprint,
            })
          })
          this.searched = true
        } catch (error) {
          console.error('Error:', error)
        }
      } else {
        this.searched = false
      }
    },
    openResult(result) {
      this.searched = false
      this.selected_search_result = result
      this.$refs['edit-modal'].toggle()
    },
    /**
     * Handles click outside of the search box, allowing for closing it when it's open.
     */
    handleClickOutside(event) {
      if (!this.$refs.searchBox.contains(event.target)) {
        this.searched = false
      }
    },
  },
}
// #endregion
// #endregion
</script>

<style lang="scss">
// #region style
.burger {
  display: inline-block;
  cursor: pointer;
}

.bar1,
.bar2,
.bar3 {
  width: 25px;
  height: 2px;
  background-color: #858585;
  margin: 6px 0;
  transition: 0.4s;
}

.change .bar1 {
  transform: rotate(-45deg) translate(-4px, 6px);
}

.change .bar2 {
  opacity: 0;
}

.change .bar3 {
  transform: rotate(45deg) translate(-5px, -8px);
}

.status {
  -webkit-animation: 2s blink ease infinite;
  -moz-animation: 2s blink ease infinite;
  -ms-animation: 2s blink ease infinite;
  -o-animation: 2s blink ease infinite;
  animation: 2s blink ease infinite;
  background-color: green;
  height: 15px;
  width: 15px;
  border-radius: 50%;
}

.search {
  width: 400px;
  max-height: 100%;
  position: relative;
  padding: 10px 10px;
  &__results {
    max-height: 500px;
    overflow-y: scroll;
    position: absolute;
    background-color: white;
    width: 400px;
    box-shadow: gray 0px 0px 4px;
    .empty {
      margin: 0;
      color: rgb(122, 122, 122);
      padding: 10px 10px;
    }
    .item {
      display: flex;
      gap: 10px;
      border-bottom: 1px solid #455a6430;
      padding: 10px 10px;
      cursor: pointer;
      &:hover {
        background-color: #455a64;
        color: white;
        .collection {
          color: rgb(189, 189, 189);
        }
      }
      .name {
        margin: 0;
      }
      .meta {
        margin: 0;
        color: rgb(122, 122, 122);
        font-size: 11px;
        display: flex;
        gap: 10px;
      }
      &:last-child {
        border-bottom: none;
      }
    }
  }
}

@keyframes blink {
  from,
  to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}
// #endregion
</style>
